import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { 
  ArrowLeft, 
  Gift,
  Copy,
  Check,
  Users,
  Trophy,
  Share2,
  Loader2,
  Crown
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ReferralPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [referralStats, setReferralStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [applyCode, setApplyCode] = useState("");
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    const storedUser = localStorage.getItem("tamba_user");
    if (!storedUser) {
      navigate("/auth");
      return;
    }
    const userData = JSON.parse(storedUser);
    setUser(userData);
    fetchData(userData.id);
  }, [navigate]);

  const fetchData = async (userId) => {
    try {
      const [statsRes, leaderboardRes] = await Promise.all([
        axios.get(`${API}/referral/${userId}`),
        axios.get(`${API}/referral/leaderboard`)
      ]);
      setReferralStats(statsRes.data);
      setLeaderboard(leaderboardRes.data || []);
    } catch (error) {
      console.error("Error fetching referral data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/auth?ref=${referralStats?.referral_code}`;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getReferralLink());
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(referralStats?.referral_code || "");
      toast.success("Referral code copied!");
    } catch (error) {
      toast.error("Failed to copy code");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: "Join Tamba",
      text: "I'm using Tamba to manage my finances with AI. Join using my referral link and we both get rewards!",
      url: getReferralLink()
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) {
      toast.error("Please enter a referral code");
      return;
    }

    setApplying(true);
    try {
      await axios.post(`${API}/referral/apply`, {
        referral_code: applyCode.trim().toUpperCase(),
        user_id: user.id
      });
      
      toast.success("Referral code applied successfully!");
      setApplyDialogOpen(false);
      setApplyCode("");
      
      // Update user in localStorage
      const updatedUser = { ...user, referred_by: "applied" };
      localStorage.setItem("tamba_user", JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid referral code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-tamba-blue" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8 relative overflow-hidden">
      {/* Decorative backgrounds */}
      <div className="absolute top-20 right-10 w-72 h-72 bg-tamba-yellow/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 left-10 w-96 h-96 bg-tamba-coral/10 rounded-full blur-3xl" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-xl font-semibold">Referral Program</h1>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6 relative">
        {/* Hero Card */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="border-tamba-yellow/30 bg-gradient-to-br from-tamba-yellow/10 via-tamba-coral/5 to-transparent overflow-hidden">
            <CardContent className="p-6 relative">
              <div className="absolute top-0 right-0 w-32 h-32 bg-tamba-yellow/20 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-tamba-yellow/20 flex items-center justify-center">
                    <Gift className="h-7 w-7 text-tamba-yellow" />
                  </div>
                  <div>
                    <h2 className="font-heading text-2xl font-bold">Earn Free Months</h2>
                    <p className="text-muted-foreground text-sm">Share Tamba, get rewarded</p>
                  </div>
                </div>
                
                <p className="text-foreground mb-6">
                  Invite friends to Tamba. When they subscribe, you get <strong className="text-tamba-coral">1 month FREE</strong> for each successful referral!
                </p>

                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="text-center p-3 bg-background/50 rounded-xl border border-tamba-blue/20">
                    <Users className="h-5 w-5 mx-auto mb-1 text-tamba-blue" />
                    <p className="font-heading text-2xl font-bold">{referralStats?.total_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Invited</p>
                  </div>
                  <div className="text-center p-3 bg-background/50 rounded-xl border border-tamba-teal/20">
                    <Trophy className="h-5 w-5 mx-auto mb-1 text-tamba-teal" />
                    <p className="font-heading text-2xl font-bold">{referralStats?.successful_referrals || 0}</p>
                    <p className="text-xs text-muted-foreground">Subscribed</p>
                  </div>
                  <div className="text-center p-3 bg-background/50 rounded-xl border border-tamba-yellow/20">
                    <Crown className="h-5 w-5 mx-auto mb-1 text-tamba-yellow" />
                    <p className="font-heading text-2xl font-bold">{referralStats?.referral_credits || 0}</p>
                    <p className="text-xs text-muted-foreground">Free Months</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Your Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">Your Referral Code</CardTitle>
              <CardDescription>Share this code with friends</CardDescription>
            </CardHeader>
            <CardContent className="pt-2 space-y-4">
              <div className="flex items-center gap-3">
                <div className="flex-1 bg-gradient-to-r from-tamba-blue/5 to-tamba-teal/5 border border-tamba-blue/20 rounded-xl p-4 text-center">
                  <p className="font-mono text-2xl font-bold tracking-widest text-tamba-blue">
                    {referralStats?.referral_code || "------"}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyCode}
                  className="h-14 w-14 rounded-xl border-tamba-blue/30 hover:bg-tamba-blue/5"
                  data-testid="copy-code-btn"
                >
                  <Copy className="h-5 w-5 text-tamba-blue" />
                </Button>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleCopyLink}
                  className="flex-1 rounded-full border-tamba-blue text-tamba-blue hover:bg-tamba-blue/5"
                  variant="outline"
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4 mr-2" />
                      Copy Link
                    </>
                  )}
                </Button>
                <Button
                  onClick={handleShare}
                  className="flex-1 rounded-full bg-tamba-yellow hover:bg-tamba-yellow/90 text-black"
                  data-testid="share-btn"
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Apply Referral Code */}
        {!user?.referred_by && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <Card className="border-border/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold mb-1">Have a referral code?</h3>
                    <p className="text-sm text-muted-foreground">
                      Apply it before subscribing to help a friend earn rewards
                    </p>
                  </div>
                  <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="rounded-full border-tamba-teal text-tamba-teal hover:bg-tamba-teal/5" data-testid="apply-code-btn">
                        Apply Code
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-heading">Enter Referral Code</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 pt-4">
                        <div>
                          <Label>Referral Code</Label>
                          <Input
                            placeholder="Enter 8-character code"
                            value={applyCode}
                            onChange={(e) => setApplyCode(e.target.value.toUpperCase())}
                            className="mt-2 font-mono text-center text-lg tracking-widest"
                            maxLength={8}
                            data-testid="referral-code-input"
                          />
                        </div>
                        <Button 
                          onClick={handleApplyCode}
                          disabled={applying || applyCode.length < 8}
                          className="w-full rounded-full bg-tamba-blue hover:bg-tamba-blue/90"
                          data-testid="submit-code-btn"
                        >
                          {applying ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Apply Referral Code"
                          )}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="font-heading text-lg flex items-center gap-2">
                  <Trophy className="h-5 w-5 text-tamba-yellow" />
                  Top Referrers
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2">
                <div className="space-y-3">
                  {leaderboard.map((entry, index) => (
                    <div 
                      key={index}
                      className={`flex items-center justify-between p-3 rounded-xl ${
                        index === 0 ? 'bg-tamba-yellow/10 border border-tamba-yellow/30' : 'bg-secondary/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                          index === 0 ? 'bg-tamba-yellow text-black' : 
                          index === 1 ? 'bg-gray-300 text-gray-700' :
                          index === 2 ? 'bg-tamba-coral text-white' :
                          'bg-secondary text-muted-foreground'
                        }`}>
                          {entry.rank}
                        </div>
                        <span className="font-medium">{entry.name}</span>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{entry.referrals} referrals</p>
                        <p className="text-xs text-muted-foreground">{entry.credits_earned} months earned</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* How It Works */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="font-heading text-lg">How It Works</CardTitle>
            </CardHeader>
            <CardContent className="pt-2">
              <div className="space-y-4">
                {[
                  { step: 1, title: "Share Your Code", desc: "Send your unique referral code or link to friends", color: "bg-tamba-blue text-white" },
                  { step: 2, title: "Friend Signs Up", desc: "They create an account using your referral", color: "bg-tamba-teal text-white" },
                  { step: 3, title: "Friend Subscribes", desc: "When they subscribe to any plan", color: "bg-tamba-yellow text-black" },
                  { step: 4, title: "You Earn Rewards", desc: "Get 1 month FREE added to your account", color: "bg-tamba-coral text-white" }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4">
                    <div className={`w-8 h-8 rounded-full ${item.color} flex items-center justify-center flex-shrink-0`}>
                      <span className="font-bold text-sm">{item.step}</span>
                    </div>
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </main>
    </div>
  );
};

export default ReferralPage;
